import {
  Box,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  InboxSidebarOption,
  InboxSidebarOptionKey,
  InboxSidebarOptions,
  OutreachContactsContext,
  ScheduledType,
} from "contexts/OutreachContacts";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import { Dispatch, SetStateAction, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { trackEvent } from "utils/tracking";

import styles from "./styles";

interface IProps {
  onSelect?: () => void;
  isSmallScreen?: boolean;
  displaySyncContacts?: boolean;
  setShowShareContactsDialog: Dispatch<SetStateAction<boolean>>;
}

export default function Sidebar({
  onSelect,
  isSmallScreen = false,
  displaySyncContacts = false,
  setShowShareContactsDialog,
}: IProps) {
  const navigate = useNavigate();
  const {
    currentSidebarOption,
    fetchContactsParams,
    handleFetchContacts,
    getSortOptions,
    totals,
  } = useContext(OutreachContactsContext);
  const { handleOpenDraftWindow } = useContext(QuickSendDrawerContext);
  const openCompose = () => {
    handleOpenDraftWindow();
    if (onSelect) {
      onSelect();
    }
  };

  const handleChange = (option: InboxSidebarOption, skipNavigation = false) => {
    trackEvent("Inbox Table Changed Folder", {
      folder: option.label,
    });
    if (onSelect) {
      onSelect();
    }
    if (option.key !== InboxSidebarOptionKey.checkins) {
      const updatedParams = { ...fetchContactsParams };
      const sortOption = getSortOptions(option)[0];
      updatedParams.page = 1;
      updatedParams.selectedSuggestion = null;
      updatedParams.importantOnly = false;
      updatedParams.fromVa = undefined;
      updatedParams.fromInstagramTracking = undefined;
      updatedParams.trackedCreatorUsernames = undefined;
      updatedParams.campaignIds = undefined;
      updatedParams.statuses = option.statuses;
      updatedParams.order = sortOption.order;
      updatedParams.orderBy = sortOption.orderBy;
      if (option.key === InboxSidebarOptionKey.scheduled) {
        updatedParams.scheduledType = ScheduledType.outreach;
      }
      if (option.key === InboxSidebarOptionKey.brandTracker) {
        updatedParams.perPage = 20;
        updatedParams.ignorePinnedFirst = true;
        updatedParams.includeBentoBrandCategories = true;
        updatedParams.source = "Brand Tracker";
      } else {
        updatedParams.perPage = undefined;
        updatedParams.ignorePinnedFirst = undefined;
        updatedParams.includeBentoBrandCategories = undefined;
        updatedParams.source = undefined;
      }
      updatedParams.searchQuery = undefined;

      handleFetchContacts(updatedParams);
    }
    if (!skipNavigation) {
      navigate(option.key);
    }
  };

  return (
    <>
      <Box
        display="flex"
        sx={[isSmallScreen && styles.smallScreenButton]}
        gap={1}
      >
        <Button
          onClick={openCompose}
          disableElevation
          size="small"
          sx={styles.button}
          variant="contained"
        >
          Compose{" "}
          <Box component="i" className="fa-regular fa-pen" sx={{ ml: 1 }} />
        </Button>
        {displaySyncContacts && (
          <Button
            onClick={() => {
              trackEvent("Inbox Table Sync Contacts Clicked");
              setShowShareContactsDialog(true);
            }}
            disableElevation
            size="small"
            sx={[
              styles.button,
              { color: "purple.dark", borderColor: "purple.dark" },
            ]}
            variant="outlined"
          >
            Sync Emails
          </Button>
        )}
      </Box>
      <List sx={{ width: "100%" }}>
        {InboxSidebarOptions.map((option) => (
          <ListItem
            key={option.key}
            sx={styles.listItem}
            disablePadding
            secondaryAction={
              <Typography
                variant="caption"
                sx={[
                  currentSidebarOption?.key === option.key && {
                    fontWeight: "bold",
                  },
                ]}
              >
                {totals[option.key]}
              </Typography>
            }
          >
            <ListItemButton
              onClick={() => handleChange(option)}
              selected={currentSidebarOption?.key === option.key}
            >
              <ListItemIcon sx={{ minWidth: "25px" }}>
                <Box component="i" className={option.icon} />
              </ListItemIcon>
              <ListItemText sx={styles.textItem} primary={option.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </>
  );
}
